/* pm-quickadd.js — size chips on a homepage product card add straight to the bag.
 *
 * Loaded DEFERRED and only on the index template. The window between a chip being
 * tappable and this file running is covered by pm-atc-guard's `card` host, which
 * queues the tap and replays it through PMCart.add once readiness is published.
 * So this file never has to defend itself against being early — it only has to be
 * correct once it is here.
 *
 * WHY THE HANDLER IS BOUND TO `document`
 * -------------------------------------
 * Three sections render quick-add cards (pm-bestsellers, pm-tee-offer, and any
 * future rail), and pm-catalog swaps its groups in place when a chip tab is
 * tapped. A handler bound to a section root stops working for whatever renders
 * after it — that is B-047's shape, and the cart upsell shipped with exactly that
 * bug on the /cart page. One delegated listener covers every host, present and
 * future.
 *
 * WHY THE FORM IS LEFT INTACT
 * ---------------------------
 * Each chip is a real submit button named `id` inside a real POST form. Without
 * JS the native submit adds the tapped size. This file calls preventDefault and
 * routes to PMCart instead; if PMCart is somehow absent the handler returns early
 * WITHOUT preventing default, so the native POST still happens and the shopper
 * still gets their add.
 */
(function () {
  'use strict';
  if (!document.addEventListener || !document.documentElement.closest) return;

  /* The provisional line is built from what is already painted on the card, so the
     bag updates on the tap rather than after two round trips. Same contract as the
     cart drawer's own upsell preview. */
  function previewFor(card, chip) {
    var img = card && card.querySelector('.pm-card__frame img');
    var t = card && card.querySelector('.pm-card__title');
    var pr = card && card.querySelector('.pm-card__price');
    var link = card && card.querySelector('.pm-card__link');
    var cents = 0;
    if (pr) {
      /* Money formatting on this store is locale-formatted; strip everything that is
         not a digit or a decimal point rather than trusting a currency symbol. A
         failed parse yields 0, which renders as a blank price for one frame and is
         corrected the moment the server's cart is adopted — never a wrong price. */
      cents = Math.round(parseFloat(String(pr.textContent).replace(/[^0-9.]/g, '')) * 100) || 0;
    }
    return {
      title: t ? t.textContent.trim() : '',
      variant: chip ? chip.textContent.trim() : '',
      price: cents,
      image: img ? img.getAttribute('src') : '',
      url: link ? link.getAttribute('href') : ''
    };
  }

  function announce(form, msg) {
    var err = form && form.querySelector('[data-pm-atc-err]');
    if (!err) return;
    err.textContent = msg;
    err.hidden = false;
  }

  function clearError(form) {
    var err = form && form.querySelector('[data-pm-atc-err]');
    if (err) { err.hidden = true; err.textContent = ''; }
  }

  document.addEventListener('click', function (ev) {
    var t = ev.target;
    if (!t || !t.closest) return;
    var chip = t.closest('[data-pm-card-add]');
    if (!chip || chip.disabled) return;
    var form = chip.closest('[data-pm-card-form]');
    if (!form) return;

    /* No cart module: fall through to the real POST. Deliberately BEFORE
       preventDefault — swallowing the event here would lose the add entirely. */
    if (!window.PMCart || !window.PMCart.add) return;

    ev.preventDefault();
    clearError(form);

    var id = chip.value;
    if (!id) { announce(form, 'Could not add that. Try again.'); return; }

    var card = chip.closest('.pm-card');
    window.PMCart.add(id, 1, chip, previewFor(card, chip), { open: true });
  });

  /* PMCart.add rejects by dispatching pm:cart:error and rolling its own optimistic
     line back; the chip's busy state is released by add()'s own finally. All that is
     left for this file is telling the shopper, in the card they tapped. There is no
     way to correlate the event back to a specific form, so the message goes to the
     most recently used one — tracked here rather than guessed at. */
  var lastForm = null;
  document.addEventListener('click', function (ev) {
    var chip = ev.target && ev.target.closest && ev.target.closest('[data-pm-card-add]');
    if (chip) lastForm = chip.closest('[data-pm-card-form]');
  }, true);

  document.addEventListener('pm:cart:error', function () {
    if (lastForm) announce(lastForm, 'That size just sold out — pick another.');
  });

  /* ---- TRIPLE-TEE offer progress -------------------------------------------
     The section renders its own first paint from Liquid, which is correct on load
     and stale the instant anything is added. Cart line items carry no collection
     data, so the section emits the eligible PRODUCT ids and the count is matched
     against those here.

     Product id, not variant id: eligibility is collection membership, and a shopper
     adding two different sizes of one tee has two qualifying units of the same
     product. Matching on variant would still work, but the id list would be five
     times longer for no gain. */
  function offerEls() {
    return [].slice.call(document.querySelectorAll('[data-pm-offer-progress]'));
  }

  function fill(tpl, n) {
    return String(tpl || '').replace('{n}', String(n));
  }

  function syncOffer(cart) {
    var els = offerEls();
    if (!els.length || !cart || !cart.items) return;
    els.forEach(function (el) {
      var raw = el.getAttribute('data-pm-offer-ids') || '';
      if (!raw) return;
      var ids = {};
      raw.split(',').forEach(function (x) { if (x) ids[x.trim()] = true; });
      var need = parseInt(el.getAttribute('data-pm-offer-need'), 10) || 0;

      var qualifying = 0;
      cart.items.forEach(function (l) {
        if (ids[String(l.product_id)]) qualifying += (l.quantity || 0);
      });

      var left = need - qualifying;
      var msg;
      if (left <= 0)          msg = el.getAttribute('data-pm-msg-done');
      else if (qualifying===0) msg = fill(el.getAttribute('data-pm-msg-none'), need);
      else                     msg = fill(el.getAttribute('data-pm-msg-some'), left);

      /* Only write when it actually changed. aria-live announces every mutation, and
         re-writing identical text on each cart render would make a screen reader
         repeat the same sentence after unrelated changes. */
      if (el.textContent.trim() !== String(msg).trim()) el.textContent = msg;
      el.setAttribute('data-pm-offer-met', left <= 0 ? 'true' : 'false');
    });
  }

  document.addEventListener('pm:cart:updated', function (ev) { syncOffer(ev.detail); });

  /* The section's server-rendered first paint is already correct, so there is no
     initial sync here — calling PMCart.state() on load would re-announce it to AT
     for no reason. */
})();
